import type { IFoldersItem } from "../../../../types";

type TreeMaps = {
    childrenByParent: Map<string, string[]>;
    parentByChild: Map<string, string>;
};
export const buildTreeRelationships = (data: IFoldersItem[]): TreeMaps => {
    const childrenMap = new Map<string, string[]>();
    const parentMap = new Map<string, string>();

    const build = (items: IFoldersItem[], parent?: string) => {
        items.forEach((item) => {
            if (parent) {
                parentMap.set(item.key, parent);
                if (!childrenMap.has(parent)) {
                    childrenMap.set(parent, []);
                }
                childrenMap.get(parent)?.push(item.key);
            }

            if (item.children) {
                build(item.children, item.key);
            }
        });
    };

    build(data);

    return {
        childrenByParent: childrenMap,
        parentByChild: parentMap,
    };
};

export const getDescendantsOnDemand = (key: string, childrenByParent: Map<string, string[]>): string[] => {
    const descendants: string[] = [];
    const children = childrenByParent.get(key) || [];

    const collectDescendants = (childKey: string) => {
        descendants.push(childKey);
        const childChildren = childrenByParent.get(childKey) || [];
        childChildren.forEach(collectDescendants);
    };

    children.forEach(collectDescendants);

    return descendants;
};

export const reconcileParentSelection = (
    selection: string[],
    changedKeys: string[],
    childrenByParent: Map<string, string[]>,
    parentByChild: Map<string, string>,
): string[] => {
    const resultSet = new Set(selection);

    const directParents = new Set<string>();
    for (const key of changedKeys) {
        const parent = parentByChild.get(key);
        if (parent) {
            directParents.add(parent);
        }
    }

    for (const parent of directParents) {
        const children = childrenByParent.get(parent);
        if (!children || children.length === 0) {
            continue;
        }

        const allChildrenSelected = children.every((child) => resultSet.has(child));

        if (allChildrenSelected) {
            resultSet.add(parent);
        } else {
            resultSet.delete(parent);
        }
    }

    if (directParents.size > 0) {
        const higherAncestors = new Set<string>();
        const maxDepth = 5;

        for (const parent of directParents) {
            let current = parentByChild.get(parent);
            let depth = 0;
            while (current && depth < maxDepth) {
                higherAncestors.add(current);
                current = parentByChild.get(current);
                depth++;
            }
        }

        for (const ancestor of higherAncestors) {
            const children = childrenByParent.get(ancestor);
            if (!children || children.length === 0) {
                continue;
            }

            const allChildrenSelected = children.every((child) => resultSet.has(child));

            if (allChildrenSelected) {
                resultSet.add(ancestor);
            } else {
                resultSet.delete(ancestor);
            }
        }
    }

    return [...resultSet];
};

export const handleSpecialCaseSelection = (
    record: IFoldersItem,
    selectedRowKeys: string[],
    siblings: string[],
    getAllDescendants: (key: string) => string[],
    reconcileParents: (selection: string[], changedKeys: string[]) => string[],
): string[] => {
    const toKeep = [record.key, ...getAllDescendants(record.key)];

    const toRemove = new Set<string>();
    const changedKeys = [record.key];
    siblings.forEach((sib) => {
        if (sib !== record.key) {
            toRemove.add(sib);
            changedKeys.push(sib);
            getAllDescendants(sib).forEach((d) => {
                toRemove.add(d);
                changedKeys.push(d);
            });
        }
    });

    let newSelection = selectedRowKeys.filter((k) => !toRemove.has(k) || toKeep.includes(k));

    newSelection = reconcileParents(newSelection, changedKeys);

    return newSelection;
};

export const handleNormalSelection = (
    record: IFoldersItem,
    willBeChecked: boolean,
    selectedRowKeys: string[],
    getAllDescendants: (key: string) => string[],
    reconcileParents: (selection: string[], changedKeys: string[]) => string[],
): string[] => {
    let newSelection: string[];
    const changedKeys = [record.key];
    const descendants = getAllDescendants(record.key);

    if (willBeChecked) {
        newSelection = [...selectedRowKeys, record.key, ...descendants];
        changedKeys.push(...descendants);
    } else {
        const toRemove = new Set([record.key, ...descendants]);
        changedKeys.push(...descendants);
        newSelection = selectedRowKeys.filter((key) => !toRemove.has(key));
    }

    newSelection = reconcileParents(newSelection, changedKeys);

    return newSelection;
};

export const getTooltipContent = (
    checked: boolean,
    record: IFoldersItem,
    parentByChild: Map<string, string>,
    childrenByParent: Map<string, string[]>,
    selectedKeysSet: Set<string>,
): string => {
    let tooltipContent = "folders.analysis.table.selection.tooltip.parentFolder";

    if (checked) {
        const parent = parentByChild.get(record.key);
        if (parent) {
            const siblings = childrenByParent.get(parent);
            if (siblings && siblings.length > 1) {
                const allSiblingsSelected = siblings.every((sib) => selectedKeysSet.has(sib));
                if (allSiblingsSelected) {
                    tooltipContent = "folders.analysis.table.selection.tooltip.subfolder";
                }
            }
        }
    }

    return tooltipContent;
};

/**
 * Rules for segment creation:
 * - If parent + ALL descendants selected → return only parent (exclude all descendants)
 * - If parent + SOME descendants selected → return only selected children (exclude parent)
 * - If only children selected (no parent) → return selected children
 */
export const getOptimizedSelectionForSegment = (
    selectedKeys: string[],
    childrenByParent: Map<string, string[]>,
    parentByChild: Map<string, string>,
): string[] => {
    if (selectedKeys.length <= 1) {
        return selectedKeys;
    }

    const selectedSet = new Set(selectedKeys);
    const result: string[] = [];
    const processed = new Set<string>();

    const sortedKeys = [...selectedKeys].sort((a, b) => a.split("/").length - b.split("/").length);

    for (const key of sortedKeys) {
        if (processed.has(key)) {
            continue;
        }

        const descendants = getDescendantsOnDemand(key, childrenByParent);
        if (!descendants || descendants.length === 0) {
            result.push(key);
            processed.add(key);
            continue;
        }

        const allSelected = descendants.every((desc) => selectedSet.has(desc));

        if (allSelected) {
            result.push(key);
            descendants.forEach((desc) => processed.add(desc));
        }
        processed.add(key);
    }

    // Add remaining children
    for (const key of selectedKeys) {
        if (processed.has(key)) {
            continue;
        }

        const parent = parentByChild.get(key);
        if (parent && result.includes(parent)) {
            continue;
        }

        result.push(key);
    }

    return result;
};
